/**
 * Crew Profile ▸ Office notes (ADR 0159; sahip 2026-11-04 B4): ofisin kişi hakkındaki
 * değerlendirme, geri bildirim ve notları. Yalnız web ofiste ve `crew_notes.view` hücresiyle;
 * kişiye ve gemiye gösterilmez.
 */

export const crewNotesEn = {
  tab: 'Office notes',
  intro:
    'Evaluations, feedback and notes written by the office. Not visible to the crew member or on board.',
  add: 'Add note',
  export: 'Export (PDF)',
  exportFailed: 'The notes could not be exported.',
  loading: 'Loading notes…',
  error: 'The notes could not be loaded.',
  empty: 'No office notes yet.',
  emptyFiltered: 'No notes of this type.',
  filter: {
    label: 'Note type',
    all: 'All',
  },
  kind: {
    evaluation: 'Evaluation',
    feedback: 'Feedback',
    note: 'Note',
  },
  meta: {
    by: '{{name}} · {{date}}',
    unknownAuthor: 'Unknown user',
    vessel: 'Vessel: {{name}}',
    contract: 'Contract: {{from}} – {{to}}',
    contractOpen: 'ongoing',
    corrected: 'Corrected (v{{version}})',
    withdrawn: 'Withdrawn',
    withdrawnReason: 'Withdrawn by {{name}} on {{date}}: {{reason}}',
    correctionReason: 'Reason: {{reason}}',
  },
  actions: {
    label: 'Actions for this note',
    correct: 'Correct',
    withdraw: 'Withdraw',
    history: 'History',
    hideHistory: 'Hide history',
  },
  history: {
    title: 'History',
    version: 'v{{version}}',
    change: {
      create: 'Written',
      correct: 'Corrected',
      withdraw: 'Withdrawn',
    },
  },
  form: {
    addTitle: 'New office note',
    correctTitle: 'Correct note',
    withdrawTitle: 'Withdraw note',
    kind: 'Type',
    body: 'Note',
    bodyHelp:
      'Not shown to the crew member. Notes are never deleted; corrections keep the old text in the history.',
    vessel: 'Vessel (optional)',
    contract: 'Contract period (optional)',
    none: 'None',
    reason: 'Reason for correction (optional)',
    withdrawReason: 'Reason for withdrawal',
    withdrawHelp:
      'The note stays in the history and in exports, marked as withdrawn. It cannot be changed again.',
    save: 'Save note',
    saveCorrection: 'Save correction',
    confirmWithdraw: 'Withdraw note',
    cancel: 'Cancel',
  },
  errors: {
    ROW_VERSION_CONFLICT: 'Someone changed this note in the meantime. Reload and try again.',
    CREW_NOTE_WITHDRAWN: 'This note has been withdrawn and cannot be changed.',
    FORBIDDEN: 'You are not allowed to do this.',
    VALIDATION_FAILED: 'Check the fields and try again.',
    GENERIC: 'The note could not be saved.',
  },
  pdf: {
    title: 'Office notes',
    confidential: 'CONFIDENTIAL – OFFICE ONLY',
    person: 'Crew member',
    crewCode: 'Crew ID',
    count: 'Notes',
    note: 'Note {{index}}',
    history: 'History',
    columns: {
      version: 'Ver.',
      change: 'Change',
      by: 'By',
      at: 'Date (UTC)',
      type: 'Type',
      text: 'Text',
      reason: 'Reason',
    },
    none: 'There are no office notes about this person.',
    basis: 'Provided on the data subject’s request (KVKK art. 11; GDPR art. 15).',
  },
};

export const crewNotesTr: typeof crewNotesEn = {
  tab: 'Ofis notları',
  intro: 'Ofisin yazdığı değerlendirme, geri bildirim ve notlar. Personele ve gemiye gösterilmez.',
  add: 'Not ekle',
  export: 'Dışa aktar (PDF)',
  exportFailed: 'Notlar dışa aktarılamadı.',
  loading: 'Notlar yükleniyor…',
  error: 'Notlar yüklenemedi.',
  empty: 'Henüz ofis notu yok.',
  emptyFiltered: 'Bu türde not yok.',
  filter: {
    label: 'Not türü',
    all: 'Tümü',
  },
  kind: {
    evaluation: 'Değerlendirme',
    feedback: 'Geri bildirim',
    note: 'Not',
  },
  meta: {
    by: '{{name}} · {{date}}',
    unknownAuthor: 'Bilinmeyen kullanıcı',
    vessel: 'Gemi: {{name}}',
    contract: 'Kontrat: {{from}} – {{to}}',
    contractOpen: 'sürüyor',
    corrected: 'Düzeltildi (s{{version}})',
    withdrawn: 'Geri çekildi',
    withdrawnReason: '{{name}} {{date}} tarihinde geri çekti: {{reason}}',
    correctionReason: 'Gerekçe: {{reason}}',
  },
  actions: {
    label: 'Bu notun işlemleri',
    correct: 'Düzelt',
    withdraw: 'Geri çek',
    history: 'Geçmiş',
    hideHistory: 'Geçmişi gizle',
  },
  history: {
    title: 'Geçmiş',
    version: 's{{version}}',
    change: {
      create: 'Yazıldı',
      correct: 'Düzeltildi',
      withdraw: 'Geri çekildi',
    },
  },
  form: {
    addTitle: 'Yeni ofis notu',
    correctTitle: 'Notu düzelt',
    withdrawTitle: 'Notu geri çek',
    kind: 'Tür',
    body: 'Not',
    bodyHelp: 'Personele gösterilmez. Notlar silinmez; düzeltmede eski metin geçmişte kalır.',
    vessel: 'Gemi (isteğe bağlı)',
    contract: 'Kontrat dönemi (isteğe bağlı)',
    none: 'Yok',
    reason: 'Düzeltme gerekçesi (isteğe bağlı)',
    withdrawReason: 'Geri çekme gerekçesi',
    withdrawHelp:
      'Not geçmişte ve dışa aktarımda "geri çekildi" olarak kalır. Bir daha değiştirilemez.',
    save: 'Notu kaydet',
    saveCorrection: 'Düzeltmeyi kaydet',
    confirmWithdraw: 'Notu geri çek',
    cancel: 'Vazgeç',
  },
  errors: {
    ROW_VERSION_CONFLICT: 'Bu not bu arada değiştirildi. Yenileyip yeniden deneyin.',
    CREW_NOTE_WITHDRAWN: 'Bu not geri çekilmiş; değiştirilemez.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    VALIDATION_FAILED: 'Alanları denetleyip yeniden deneyin.',
    GENERIC: 'Not kaydedilemedi.',
  },
  pdf: {
    title: 'Ofis notları',
    confidential: 'GİZLİ – YALNIZ OFİS',
    person: 'Personel',
    crewCode: 'Crew ID',
    count: 'Not sayısı',
    note: 'Not {{index}}',
    history: 'Geçmiş',
    columns: {
      version: 'Sür.',
      change: 'Değişiklik',
      by: 'Yazan',
      at: 'Tarih (UTC)',
      type: 'Tür',
      text: 'Metin',
      reason: 'Gerekçe',
    },
    none: 'Bu kişi hakkında ofis notu yok.',
    basis: 'İlgili kişinin talebiyle verilmiştir (KVKK m.11; GDPR md. 15).',
  },
};
